import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Input, Form, theme, Col, notification } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { globalOp } from 'store/ducks/Global';
import { logoSrc } from 'components/TitleBar/TitleBar.styled';
import { set_superAdmin } from 'store/ducks/Global/globalSlice';
import * as Styled from './Login.styled';

const { useToken } = theme;

const LoginForm: React.FC = () => {
  const dispatch = useDispatch();
  const { token } = useToken();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [passwordHints, setPasswordHints] = useState<string[]>([]);

  const getPasswordHints = (password: string) => {
    const hints: string[] = [];

    if (!password) return [];

    if (password.length < 8)
      hints.push('Password must be at least 8 characters long');

    if (!/[A-Za-z]/.test(password))
      hints.push('Password must include at least one letter');

    if (!/[0-9]/.test(password))
      hints.push('Password must include at least one digit');

    if (!/[@$!%*?&]/.test(password))
      hints.push(
        'Password must include at least one special character (@ $ ! % * ? &)',
      );

    const validPattern = /^[A-Za-z0-9@$!%*?&]+$/;
    if (password.length > 0 && !validPattern.test(password))
      hints.push('Password contains invalid characters');

    return hints;
  };

  const handleSubmit = (values: { email: string; password: string }) => {
    setLoading(true);
    dispatch(set_superAdmin(false));
    const { email, password } = values;
    globalOp
      .login(email, password)
      .then((response) => {
        notification.info({
          message: response.data.message,
          placement: 'topRight',
          duration: 20,
        });
      })
      .catch((error) => {
        notification.error({
          message: '',
          description:
            error?.response?.data?.error ||
            'An error occurred. Please try again.',
          placement: 'topRight',
          duration: 10,
        });
      })
      .finally(() => setLoading(false));
  };

  const handleSuperAdminLogin = async () => {
    dispatch(set_superAdmin(true));

    const values = await form.validateFields();
    globalOp
      .superAdminLogin(values.email, values.password)
      .then((response) => {
        notification.info({
          message: response.data.message,
          placement: 'topRight',
          duration: 20,
        });
      })
      .catch((error) => {
        notification.error({
          message: 'Info',
          description:
            error?.response?.data?.error ||
            'An error occurred. Please try again.',
          placement: 'topRight',
          duration: 10,
        });
      });
  };

  return (
    <Styled.LoginContainer>
      <Styled.NavbarLogin>
        <Col span={6}>
          <img src={logoSrc} alt="logo" />
        </Col>
        <Col span={18}>
          <h1>ProgRisCom – DORA ICT & Third-Party Risk Platform</h1>
          <p>
            <span>✔ Register of Information (Art. 28)</span>
            <span>✔ ICT Governance & Risk (Art. 8)</span>
          </p>
        </Col>
      </Styled.NavbarLogin>
      <Styled.FormContainer>
        <Form
          form={form}
          name="login"
          onFinish={handleSubmit}
          autoComplete="on"
        >
          <Styled.ItemsContainer>
            <Styled.StyledFormItem
              token={token}
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Please input your email!' },
                {
                  type: 'email',
                  message: 'The input is not valid email!',
                },
              ]}
            >
              <Input
                prefix={<MailOutlined className="site-form-item-icon" />}
                type="email"
                autoComplete="email"
              />
            </Styled.StyledFormItem>
            <Styled.StyledFormItem
              token={token}
              label="Password"
              name="password"
              rules={[
                { required: true, message: 'Please input your password!' },
              ]}
              help={passwordHints.length ? passwordHints.join(', ') : undefined}
              validateStatus={passwordHints.length ? 'warning' : 'success'}
            >
              <Input.Password
                autoComplete="current-password"
                onChange={(e) =>
                  setPasswordHints(getPasswordHints(e.target.value))
                }
              />
            </Styled.StyledFormItem>
          </Styled.ItemsContainer>
          <Styled.BottomSection>
            <Styled.StyledButton
              type="primary"
              loading={loading}
              htmlType="submit"
            >
              Log In
            </Styled.StyledButton>
            <Styled.StyledButton
              type="primary"
              htmlType="button"
              loading={loading}
              onClick={handleSuperAdminLogin}
            >
              Log in as a super admin
            </Styled.StyledButton>
            <Styled.StyledLink to="/forgot_password" token={token}>
              <Styled.StyledButton type="primary" htmlType="button">
                Reset password
              </Styled.StyledButton>
            </Styled.StyledLink>
          </Styled.BottomSection>
        </Form>
      </Styled.FormContainer>
    </Styled.LoginContainer>
  );
};

export default LoginForm;
